import { useWhoami } from "@/api/auth";
import type { WhoamiResponse } from "@/api/types";

function canMutateFromWhoami(data: WhoamiResponse | undefined): {
  canMutate: boolean;
  reason: string | null;
} {
  if (!data) {
    return { canMutate: false, reason: "Checking session permissions..." };
  }
  if (!data.auth_required) {
    return { canMutate: true, reason: null };
  }
  if (data.role === "admin" || data.role === "user") {
    return { canMutate: true, reason: null };
  }
  return {
    canMutate: false,
    reason: "Insufficient permissions for this action.",
  };
}

function canManageBackendsFromWhoami(data: WhoamiResponse | undefined): {
  allowed: boolean;
  reason: string | null;
} {
  if (!data) {
    return { allowed: false, reason: "Checking session permissions..." };
  }
  if (!data.auth_required) {
    return { allowed: true, reason: null };
  }
  if (data.role === "admin") {
    return { allowed: true, reason: null };
  }
  return {
    allowed: false,
    reason: "Only admins can manage backends.",
  };
}

function canManageUsersFromWhoami(data: WhoamiResponse | undefined): {
  allowed: boolean;
  reason: string | null;
} {
  if (!data) {
    return { allowed: false, reason: "Checking session permissions..." };
  }
  if (!data.auth_required) {
    return { allowed: true, reason: null };
  }
  if (data.role === "admin") {
    return { allowed: true, reason: null };
  }
  return {
    allowed: false,
    reason: "Only admins can manage users.",
  };
}

export function usePermissions() {
  const whoami = useWhoami();
  const { canMutate, reason } = canMutateFromWhoami(whoami.data);
  const backends = canManageBackendsFromWhoami(whoami.data);
  const users = canManageUsersFromWhoami(whoami.data);
  return {
    isLoading: whoami.isLoading,
    role: whoami.data?.role ?? null,
    canMutate,
    mutationReason: reason,
    canManageBackends: backends.allowed,
    backendReason: backends.reason,
    canManageUsers: users.allowed,
    usersReason: users.reason,
  };
}
